/**
 * Payload Builder Helpers for CreateBlankModal
 * Extracted to reduce cognitive complexity of main component
 */

// Helper: Build base fields common to all submission types
export const buildBaseFields = (formData, userId, isAutoRenewal, parentNode) => {
  const baseFields = {
    contract_title: formData.contractTitle,
    owner_department_id: formData.ownerDepartment,
    start_date: formData.startDate,
    status_id: formData.status_id ? Number(formData.status_id) : null,
    contract_type: Number(formData.contractType),
    region_id: formData.region_id ? Number(formData.region_id) : null,
    project_id: formData.project_id ? Number(formData.project_id) : null,
    created_by: Number(userId),
    updated_by: Number(userId),
    user_id: Number(userId),
    is_deleted: 0,
    is_favourite: 0,
    is_metadata_required: true,
    description: formData.description || "",
    po_number: formData.po_number || null,
    net_term: formData.net_term || null,
    bill_rate_currency: formData.bill_rate_currency || null,
    billing_country: formData.billing_country || null,
    termination_date: !isAutoRenewal ? formData.termination_date : null,
    termination_reason: formData.termination_reason || null,
    billing_type: formData.billing_type || null,
    parent_contract_id: formData.parent_contract_id || null,
    parent_contract_type: parentNode?.data?.type === "Folder" ? 1 : 0,
    metatag: formData.metatag || null,
    contract_value: formData.contract_value || null,
    currency_type: formData.currency_type || null,
    cr_entity: formData.subsidiary || null,
    billing_frequency: formData.billing_frequency || null,
    execution_type: Array.isArray(formData.execution_type) ? formData.execution_type.join(',') : (formData.execution_type || null),
    hsn_code: formData.hsn_code || null,
    tax_code: formData.tax_code || null,
  };

  return baseFields;
};

// Helper: Add business-specific fields
export const addBusinessTypeFields = (fields, formData, contractSelection, clients, vendors) => {
  const updated = { ...fields };
  updated.existing_contract = contractSelection === 'existing';
  updated.type = formData.businessType;

  if (contractSelection === 'existing' && formData.year) {
    updated.year = formData.year;
  }

  if (formData.businessType === 'Client') {
    updated.client_id = Number(formData.clientName);
    updated.client_email = formData.clientEmail;
    updated.vendor_id = 0;
  } else if (formData.businessType === 'Vendor') {
    updated.vendor_id = Number(formData.vendorName);
    updated.client_id = 0;
  }

  return updated;
};

// Helper: Add amendment/addendum fields
export const addAmendmentFields = (fields, formData, isAmendment, isAddendum) => {
  const updated = { ...fields };

  if (isAmendment && formData.parent_contract_id) {
    updated.is_amendment = 'True';
  }
  if (isAddendum && formData.parent_contract_id) {
    updated.is_addendum = 'True';
  }

  return updated;
};

// Helper: Add renewal fields
export const addRenewalFields = (fields, formData, isAutoRenewal) => {
  const updated = { ...fields };

  if (isAutoRenewal) {
    updated.end_date = formData.endDate;
    updated.renewal_date = '1999-01-01';
    updated.auto_renewal = 'TRUE';
  } else {
    updated.end_date = formData.endDate;
    updated.renewal_date = formData.renewalDate || null;
  }

  return updated;
};

// Helper: Clean optional parent contract
export const cleanParentContractIfNeeded = (fields, isAmendment, isAddendum, formData) => {
  const updated = { ...fields };
  if (!isAmendment && !isAddendum && !formData.parent_contract_id) {
    delete updated.parent_contract_id;
  }
  return updated;
};

// Helper: Build upload payload
export const buildUploadPayload = (
  formData,
  userId,
  isAutoRenewal,
  parentNode,
  contractSelection,
  isAmendment,
  isAddendum,
  itemDetails = [],
  sowDetails = []
) => {
  let payload = buildBaseFields(formData, userId, isAutoRenewal, parentNode);
  payload.creation_type = "upload";
  payload = addBusinessTypeFields(payload, formData, contractSelection);
  payload = addAmendmentFields(payload, formData, isAmendment, isAddendum);
  payload = addRenewalFields(payload, formData, isAutoRenewal);
  payload = cleanParentContractIfNeeded(payload, isAmendment, isAddendum, formData);
  payload.item_details = itemDetails;
  payload.sow_details = sowDetails;
  return payload;
};

// Helper: Build template payload
export const buildTemplatePayload = (
  formData,
  userId,
  isAutoRenewal,
  parentNode,
  contractSelection,
  isAmendment,
  isAddendum,
  selectedTemplate,
  itemDetails = [],
  sowDetails = []
) => {
  let payload = buildBaseFields(formData, userId, isAutoRenewal, parentNode);
  payload.creation_type = "template";
  payload.template_id = selectedTemplate.template_id;
  payload.template_name = selectedTemplate?.template_name || "";
  payload = addBusinessTypeFields(payload, formData, contractSelection);
  payload = addAmendmentFields(payload, formData, isAmendment, isAddendum);
  payload = addRenewalFields(payload, formData, isAutoRenewal);
  payload = cleanParentContractIfNeeded(payload, isAmendment, isAddendum, formData);
  payload.item_details = itemDetails;
  payload.sow_details = sowDetails;
  return payload;
};

// Helper: Build blank payload
export const buildBlankPayload = (
  formData,
  userId,
  isAutoRenewal,
  parentNode,
  contractSelection,
  isAmendment,
  isAddendum,
  itemDetails = [],
  sowDetails = []
) => {
  let payload = buildBaseFields(formData, userId, isAutoRenewal, parentNode);
  payload.creation_type = "blank";
  payload.is_deleted = 0;
  payload.is_favourite = 0;
  payload.priority_template_id = 2;
  payload.current_stage_id = 1;
  payload = addBusinessTypeFields(payload, formData, contractSelection);
  payload = addAmendmentFields(payload, formData, isAmendment, isAddendum);
  payload = addRenewalFields(payload, formData, isAutoRenewal);
  payload = cleanParentContractIfNeeded(payload, isAmendment, isAddendum, formData);
  payload.item_details = itemDetails;
  payload.sow_details = sowDetails;
  return payload;
};
